import { isCapacitorPlatform } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { USER_EVENTS } from "@src/constants";
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Deploy } from "cordova-plugin-ionic";
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { CheckForUpdateResponse } from "cordova-plugin-ionic/dist/IonicCordova";
// eslint-disable-next-line no-restricted-imports
import { useCallback, useEffect, useRef, useState } from "react";

import { configureDeploy, getOtaDownloadMetrics } from "./utils";

type OtaCheckStatus = "not-started" | "in-progress" | "done";

export function useOtaUpdate() {
  const [otaUpdateInfo, setOtaUpdateInfo] = useState<CheckForUpdateResponse>();
  const [otaUpdateProgress, setOtaUpdateProgress] = useState(0);

  const otaCheckStatusRef = useRef<OtaCheckStatus>("not-started");

  const checkForOtaUpdate = useCallback(async () => {
    try {
      otaCheckStatusRef.current = "in-progress";

      await configureDeploy();
      logEvent(USER_EVENTS.OTA_UPDATE_CHECKING_FOR_UPDATE);
      const updateInfo = await Deploy.checkForUpdate();

      if (!updateInfo?.available) {
        logEvent(USER_EVENTS.OTA_UPDATE_NOT_AVAILABLE);
      } else {
        logEvent(USER_EVENTS.OTA_UPDATE_DOWNLOAD_STARTED, {
          buildId: updateInfo.build,
        });

        const startTime = new Date();
        await Deploy.downloadUpdate((progress) => {
          if (progress && progress % 5 === 0) {
            setOtaUpdateProgress(progress);
          }
        });

        const extractionStartTime = new Date();
        await Deploy.extractUpdate();
        const endTime = new Date();
        const { downloadOnlyTimeInSeconds, extractionTimeInSeconds, totalTimeInSeconds } =
          getOtaDownloadMetrics(extractionStartTime, startTime, endTime);

        logEvent(USER_EVENTS.OTA_UPDATE_DOWNLOADED, {
          buildId: updateInfo.build,
          // @deprecated. Used for metrics right now, but should move to `totalTimeTakenInSeconds` for newer metrics
          downloadTimeSeconds: totalTimeInSeconds,
          downloadOnlyTimeInSeconds,
          extractionTimeInSeconds,
          totalTimeInSeconds,
        });

        setOtaUpdateInfo(updateInfo);
      }
      otaCheckStatusRef.current = "done";
    } catch (error) {
      logEvent(USER_EVENTS.OTA_UPDATE_DOWNLOAD_ERROR, { error });
      otaCheckStatusRef.current = "done";
    }
  }, []);

  useEffect(() => {
    const shouldCheckForUpdate =
      otaCheckStatusRef.current === "not-started" && isCapacitorPlatform();
    if (shouldCheckForUpdate) {
      // downloaded build will be auto applied when worker reopens the app
      void checkForOtaUpdate();
    }
  }, [checkForOtaUpdate]);

  return {
    otaUpdateInfo,
    otaUpdateProgress,
  };
}
